import { FC } from 'react'
import { EyebrowLG } from '@/atoms/Text'
import { getWebClient } from '@/services/ApolloClient'
import { useGoal } from '@/services/GoalService'
import { useLocale } from '@/utils/LocaleUtil'

interface Props {
  slug: string
}

export const CollectiblesGoalTrackerBreakdown: FC<Props> = ({ slug }) => {
  const { locale } = useLocale()
  const client = getWebClient({ locale })

  const goalTracker = useGoal({ slug, client })

  const totalGoal = goalTracker?.costRaw ?? 0
  const raised = goalTracker?.raisedRaw ?? 0

  const { currentStage, percentComplete } = calculateStageDetails(raised, totalGoal)

  if (totalGoal === 0) return null

  return (
    <div className="my-4">
      <div className="my-2 w-full rounded-full bg-core-gray-900">
        <div
          className="h-2.5 rounded-full bg-oxide-700"
          style={{
            width: `${(percentComplete || 0) > 100 ? '100%' : percentComplete ?? 0}%`,
          }}
        />
      </div>
      <div className="mx-auto flex flex-wrap justify-between">
        <EyebrowLG className="@sm:photon-eyebrow-sm block">
          <b>
            Funding {goalTracker?.groupName} {goalTracker?.name}
          </b>{' '}
        </EyebrowLG>
        <EyebrowLG className="block text-core-gray-700">
          {currentStage} - {percentComplete.toFixed(0)}% Complete
        </EyebrowLG>
      </div>
    </div>
  )
}

const calculateStageDetails = (raised: number, totalGoal: number) => {
  const stages = [
    { name: 'Development', percentage: 0.1 },
    { name: 'Pre-Production', percentage: 0.1 },
    { name: 'Production', percentage: 0.4 },
    { name: 'Post-Production', percentage: 0.2 },
    { name: 'Marketing', percentage: 0.2 },
  ]

  let accumulatedCost = 0
  let currentStage = stages[0]?.name
  let percentComplete = 0
  let stageCost = 0

  for (const stage of stages) {
    stageCost = totalGoal * stage.percentage
    accumulatedCost += stageCost

    if (raised < accumulatedCost) {
      currentStage = stage.name
      percentComplete = ((raised - (accumulatedCost - stageCost)) / stageCost) * 100
      break
    }
  }

  return { currentStage, percentComplete, stageCost }
}

// Development (5%-10%): This initial phase includes the costs of rights acquisition, screenplay development, and preliminary budgeting and scheduling. Funds are also allocated for initial location scouting and conceptual designs.
// Pre-production (10%-20%): Pre-production involves finalizing the script, casting, hiring crew, choosing locations, and detailed planning of the shoot. This stage also includes the creation of sets, costumes, and special effects planning.
// Production (40%-50%): The production phase is where the bulk of the budget is often spent. This includes all costs associated with filming: salaries for the cast and crew, set construction and operation, costumes, catering, transportation, and equipment rental. This phase is the most labor and resource-intensive part of the movie-making process.
// Post-production (20%-30%): After filming concludes, the post-production phase begins. This includes editing, sound mixing, adding special effects, color correction, and incorporating the musical score. It can also involve reshoots if necessary.
// Marketing and Distribution (20%-30%): The final phase involves marketing the movie to audiences through advertising, promotional events, and distribution both in theaters and through various digital platforms. This stage is critical for ensuring the movie reaches its audience and achieves financial success.
